'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useMeasure, useMedia } from 'react-use';

import { AdaptiveLogo } from '@/components/adaptive-logo';
import VideoPlayer from '@/components/videoplayer';

import { CaseStudyWrapper } from '../components/CaseStudyWrapper';
import { ImageData, ImageSlideshow } from '../components/ImageSlideshow';


export const ClientPage = () => {
  const isTablet = useMedia('(min-width: 768px)', false);
  const carouselImages: ImageData[] = [
    {
      src: '/images/mmhmm/slideshow_1.jpg',
      width: 3242,
      height: 1712,
      alt: 'Screenshot of Airtime’s dynamic video interface with presenter and slides visible.',
    },
    {
      src: '/images/mmhmm/slideshow_2.jpg',
      width: 3242,
      height: 1712,
      alt: 'Airtime team collaborating remotely, sharing ideas on a group video call.',
    },
    {
      src: '/images/mmhmm/slideshow_3.jpg',
      width: 3242,
      height: 1712,
      alt: 'Airtime product demo showing interactive features for engaging presentations.',
    },
    {
      src: '/images/mmhmm/slideshow_4.jpg',
      width: 3242,
      height: 1712,
      alt: 'Airtime user customizing their video background and layout during a call.',
    },
    {
      src: '/images/mmhmm/slideshow_1.jpg',
      width: 3242,
      height: 1712,
      alt: 'Screenshot of Airtime’s dynamic video interface with presenter and slides visible.',
    },
    {
      src: '/images/mmhmm/slideshow_2.jpg',
      width: 3242,
      height: 1712,
      alt: 'Airtime team collaborating remotely, sharing ideas on a group video call.',
    },
    {
      src: '/images/mmhmm/slideshow_3.jpg',
      width: 3242,
      height: 1712,
      alt: 'Airtime product demo showing interactive features for engaging presentations.',
    },
    {
      src: '/images/mmhmm/slideshow_4.jpg',
      width: 1942,
      height: 1712,
      alt: 'Airtime user customizing their video background and layout during a call.',
    },
  ];

  return (
    <CaseStudyWrapper
      mobileBackgroundImage="/images/mmhmm/case_hero_mobile.png"
      backgroundImage="/images/mmhmm/case_hero.png"
      heading="Airtime"
      headingColor="text-white"
      backgroundColor="bg-airtime"
      nextLink="/carrot"
      hoverTextColor="text-airtime"
      backgroundPosition="object-center"
      overlayImages={[
        {
          src: '/images/mmhmm/case_overlay_UI.png',
          alt: 'Airtime user interface overlay showing video controls and participant thumbnails',
          className: 'top-[35%] left-[15%]',
        },
        {
          src: '/images/mmhmm/case_overlay_Demo.png',
          alt: 'Airtime demo overlay highlighting interactive presentation features',
          className: 'bottom-[31%] right-[18.85%]',
        },
        {
          src: '/images/mmhmm/case_overlay_PDF.png',
          alt: 'Airtime PDF sharing overlay illustrating document collaboration in a call',
          className: 'bottom-[48%] right-[24%]',
        },
      ]}
      mobileOverlayImages={[
        {
          src: '/images/mmhmm/case_overlay_mobile_UI.png',
          alt: 'Airtime mobile user interface overlay with video controls and participant thumbnails',
          className: 'bottom-[20%] left-5 w-[195px] h-[234px]',
        },
      ]}
    >
      <Link href="/">
        <AdaptiveLogo blendMode={false} aria-label="Go to All Turtles Home" />
      </Link>
      <div className="relative z-20">
        <div className="bg-[#1D8C98] pb-[84px] pt-[84px] tablet:pb-[164px] tablet:pt-[65px]">
          <div className="static-grid">
            <div className="col-span-full">
              <p className="mb-[45px] hidden justify-center font-breit text-[clamp(16px,calc(16px+0.875vw),30px)] font-normal uppercase leading-none tracking-wider text-[#FCFBC9] tablet:block">
                Airtime
              </p>

              <div className="col-span-full grid grid-cols-12 mobile-only:space-y-[32px]">
                <div className="col-span-full justify-start text-white mobile-only:space-y-[32px] tablet:col-span-8 tablet:space-y-[45px]">
                  <p className="font-soehne text-[clamp(18px,calc(18px+1.625vw),44px)] font-normal leading-[140%] tracking-[0.2098]">
                    Remember the chaos of 2020? We sure do. 
                  </p>
                  <p className="font-soehne text-[clamp(18px,calc(18px+1.625vw),44px)] font-normal leading-[140%] tracking-[0.2098]">
                    Overnight, companies around the world had to adopt new ways of working, throwing out the old
                    playbook. There were positives: We were empowered to work from anywhere, hire from everywhere, and
                    restructure our time. But there were also negatives, as whole days vanished into an endless stream
                    of boring video calls.
                  </p>
                  <p className="font-soehne text-[clamp(18px,calc(18px+1.625vw),44px)] font-normal leading-[140%] tracking-[0.2098]">
                    So we founded a company to fix it.
                  </p>
                </div>

                <div className="col-span-full tablet:col-span-3 tablet:col-start-10">
                  <p className="font-soehne text-[clamp(16px,calc(16px+0.125vw),18px)] font-buch leading-[200%] tracking-[0.21] text-[#FCFBC9]">
                    We provided
                  </p>
                  <ul className="list-none font-soehne text-[clamp(16px,0.259vw,18px)] font-normal uppercase leading-[150%] tracking-[0.21] text-white tablet:col-span-2 tablet:col-start-11 desktop:mt-0 desktop:leading-[200%]">
                    <li>Brand + Design</li>
                    <li>Product + Engineering</li>
                    <li>Intellectual Property</li>
                    <li>Finance</li>
                    <li>Recruiting</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white pb-[84px] pt-[84px] tablet:pb-[144px] tablet:pt-[256px]">
          <div className="static-grid gap-y-[36px] text-navy">
            <div className="col-span-full space-y-[36px] tablet:col-span-5">
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                Airtime started out as mmhmm, a tool that allowed people to be more dynamic and stay on screen with
                their slides. It sprang from the belief that video should feel as effortless as sending a text message
                and as human as a coffee break chat.
              </p>
              <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                Lots of people saw the potential, and mmhmm soon had backing from Sequoia Capital, Softbank Vision Fund,
                and others. As the team and the product have evolved, All Turtles has continued to provide critical
                infrastructure and embedded team support across functions, including product and brand design,
                engineering, finance, recruiting, and IP management.
              </p>
            </div>
            <div className="col-span-full grid place-items-start tablet:col-span-6 tablet:col-start-7">
              {isTablet ? (
                <Image
                  src="/images/mmhmm/case_camera.png"
                  alt="Airtime camera interface showing presenter and slides in a video call"
                  width={2235}
                  height={1386}
                  priority
                />
              ) : (
                <Image
                  src="/images/mmhmm/airtime_camera_mobile.png"
                  alt="Airtime mobile camera interface with presenter and slides"
                  width={1320}
                  height={1296}
                  priority
                />
              )}
            </div>

            <div className="col-span-full">
              <p className="font-soehne text-[clamp(24px,calc(24px+1.25vw),44px)] leading-[172%] text-airtime tablet:pt-[92px]">
                Today, video is more prevalent than ever. It powers distributed teams, the creator economy, online
                education, sales, and more. But one thing hasn’t changed: smarter work starts with better communication.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#003852] bg-gradient-to-b from-[rgba(0,56,82,0.20)] to-[rgba(29,140,152,0.20)] pb-[18px] pt-[72px] text-white tablet:pb-[166px] tablet:pt-[126px]">
          <div className="tablet:static-grid mobile-only:grid mobile-only:grid-cols-12">
            <div className="col-span-full tablet:col-span-10 tablet:col-start-2">
              <h2 className="mb-[36px] px-h-margin-mobile text-center text-[clamp(36px,calc(36px+1.625vw),62px)] font-halbfett leading-[106.452%] tracking-[-1.24px] tablet:mb-[60px] tablet:px-0">
                Limitless looks for every call
              </h2>
              <div className="grid grid-cols-3 gap-[12px] tablet:grid-cols-5 tablet:grid-rows-3">
                <div className="h-full w-full">
                  <Image
                    src="/images/mmhmm/look_01.png"
                    alt="Airtime virtual background: modern office setting"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="h-full w-full">
                  <Image
                    src="/images/mmhmm/look_02.png"
                    alt="Airtime virtual background: creative studio"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="h-full w-full">
                  <Image
                    src="/images/mmhmm/look_03.png"
                    alt="Airtime virtual background: cozy home environment"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="h-full w-full">
                  <Image
                    src="/images/mmhmm/look_04.png"
                    alt="Airtime virtual background: minimalist workspace"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="h-full w-full">
                  <Image
                    src="/images/mmhmm/look_05.png"
                    alt="Airtime virtual background: colorful abstract design"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="h-full w-full">
                  <Image
                    src="/images/mmhmm/look_06.png"
                    alt="Airtime virtual background: cityscape view"
                    width={684}
                    height={384}
                  />
                </div>

                {/* everything below this point is hidden on mobile, but shows up again on tablet+ */}
                <div className="hidden h-full w-full tablet:block">
                  <Image
                    src="/images/mmhmm/look_07.png"
                    alt="Airtime virtual background: nature landscape"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="hidden h-full w-full tablet:block">
                  <Image
                    src="/images/mmhmm/look_08.png"
                    alt="Airtime virtual background: geometric shapes"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="hidden h-full w-full tablet:block">
                  <Image
                    src="/images/mmhmm/look_09.png"
                    alt="Airtime virtual background: tech-inspired theme"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="hidden h-full w-full tablet:block">
                  <Image
                    src="/images/mmhmm/look_10.png"
                    alt="Airtime virtual background: soft pastel colors"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="hidden h-full w-full tablet:block">
                  <Image
                    src="/images/mmhmm/look_11.png"
                    alt="Airtime virtual background: bold graphic pattern"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="hidden h-full w-full tablet:block">
                  <Image
                    src="/images/mmhmm/look_12.png"
                    alt="Airtime virtual background: playful illustration"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="hidden h-full w-full tablet:block">
                  <Image
                    src="/images/mmhmm/look_13.png"
                    alt="Airtime virtual background: elegant conference room"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="hidden h-full w-full tablet:block">
                  <Image
                    src="/images/mmhmm/look_14.png"
                    alt="Airtime virtual background: vibrant color blocks"
                    width={684}
                    height={384}
                  />
                </div>
                <div className="hidden h-full w-full tablet:block">
                  <Image
                    src="/images/mmhmm/look_15.png"
                    alt="Airtime virtual background: futuristic digital art"
                    width={684}
                    height={384}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white pb-[84px] pt-[84px] tablet:py-[196px]">
          <div className="static-grid">
            <div className="col-span-full grid grid-cols-12 gap-y-[36px]">
              <div className="col-span-full grid place-items-start tablet:col-span-6">
                {isTablet ? (
                  <Image
                    src="/images/mmhmm/airtime_screen_recorder.png"
                    alt="Airtime screen recorder interface for capturing presentations"
                    width={2235}
                    height={1386}
                  />
                ) : (
                  <Image
                    src="/images/mmhmm/airtime_screen_recorder_mobile.png"
                    alt="Airtime mobile screen recorder interface"
                    width={1320}
                    height={1296}
                  />
                )}
              </div>
              <div className="col-span-full tablet:col-span-5 tablet:col-start-8">
                <h2 className="mb-2 font-heldane text-[clamp(24px,calc(24px+1.25vw),44px)] tracking-[0.21px] text-airtime tablet:mb-[36px] tablet:mb-[44px]">
                  Professional, but personal
                </h2>
                <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                  In creating the initial mmhmm brand, we leaned into individuality and a sense of fun, centered on an
                  arresting name that prompted response and a playful visual language unlike most workplace tools. But
                  there was a deeper message, too. By breaking down barriers between people and content, the product
                  fostered attention, engagement, and understanding.
                </p>
              </div>
            </div>
            <div className="col-span-full grid grid-cols-12 gap-y-[36px] pt-[36px] tablet:pt-[196px]">
              <div className="order-2 col-span-full space-y-[36px] tablet:order-1 tablet:col-span-5">
                <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                  When the company reinvented itself as Airtime in 2025, we balanced that ethos with a boldly
                  professional and people-forward brand that stripped everything down to its essence. Instead of calling
                  attention to the app, we focused on what it unlocks.
                </p>
                <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                  The evolution to Airtime also brought a new approach to the product itself. Instead of adding more
                  features and complexity to one tool, Airtime is more like a utility belt, loaded with easy to learn,
                  simple to use tools tailored to whatever you need to get your message across on video right now.
                </p>
              </div>

              <div className="order-1 col-span-full grid place-items-center tablet:order-2 tablet:col-span-6 tablet:col-start-7">
                <VideoPlayer
                  sources={[{ src: '/videos/airtime-video.mp4' }]}
                  controls={false}
                  loop={true}
                  gif="/videos/airtime-video.gif"
                />
              </div>
            </div>
            <div className="col-span-full pt-[36px] tablet:pt-[196px]">
              {isTablet ? (
                <Image
                  src="/images/mmhmm/airtime_stacks.png"
                  alt="Airtime stacks feature showing layered video and content windows"
                  width={4470}
                  height={2280}
                />
              ) : (
                <Image
                  src="/images/mmhmm/airtime_stacks_mobile.png"
                  alt="Airtime mobile stacks feature with layered video and content"
                  width={1320}
                  height={1452}
                />
              )}
            </div>
            <div className="col-span-full grid grid-cols-12 pt-[36px] tablet:pt-[196px]">
              <div className="order-2 col-span-full grid place-items-center py-10 tablet:order-1 tablet:col-span-6">
                {isTablet ? (
                  <Image
                    src="/images/mmhmm/airtime_newcreator.png"
                    alt="Airtime creator tool for video production"
                    width={2235}
                    height={1386}
                  />
                ) : (
                  <Image
                    src="/images/mmhmm/airtime_creator_mobile.png"
                    alt="Airtime mobile creator tool for video production"
                    width={1320}
                    height={1296}
                  />
                )}
              </div>
              <div className="order-1 col-span-full grid place-items-center space-y-[36px] tablet:order-2 tablet:col-span-5 tablet:col-start-8">
                <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                  From improving your look or sharing ideas on the fly to full-featured video creation, Airtime’s
                  growing kit of essential tools is constantly adapting to the evolving needs of always-online
                  professionals, making video communication a delight instead of a burden.
                </p>
                <p className="font-soehne text-[clamp(18px,calc(18px+0.375vw),24px)] leading-[150%] tracking-[0.21px]">
                  No matter where you show up on video, Airtime helps you bridge the gap between you, your message, and
                  your audience. You’ll discover superpowers you never knew you had: to have more control over your
                  time, make stronger connections over video, and be a better communicator. Because that benefits
                  everyone.
                </p>
              </div>
            </div>
            <div className="col-span-full">
              <p className="font-soehne text-[clamp(24px,calc(24px+1.25vw),44px)] leading-[172%] text-airtime tablet:pt-[92px]">
                All Turtles’ vision is simple: useful products that address real-world problems through simple business
                models. Airtime delivers in all three aspects.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#1D8C98] py-12">
          <ImageSlideshow
            images={carouselImages}
            aspectRatio="aspect-[16/9]"
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
          />
        </div>
      </div>
    </CaseStudyWrapper>
  );
};
